// src/pages/RegisterPage.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import api from '../api';

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    firstName: '',
    lastName: '',
    email: '',
    mobileNumber: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  // Toggle states
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { firstName, lastName, email, mobileNumber, password, confirmPassword } = formData;

  const onChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Clear specific field error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }

    // Clear general error
    if (errors.general) {
      setErrors({ ...errors, general: null });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!firstName.trim()) newErrors.firstName = 'Required';
    if (!lastName.trim()) newErrors.lastName = 'Required';

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      newErrors.email = 'Required';
    } else if (!emailRegex.test(email)) {
      newErrors.email = 'Invalid email';
    }

    // Password validation
    if (!password) {
      newErrors.password = 'Required';
    } else if (password.length < 6) {
      newErrors.password = 'Min 6 characters';
    }

    // Confirm password validation
    if (!confirmPassword) {
      newErrors.confirmPassword = 'Required';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    return newErrors;
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setLoading(true);
    setErrors({});
    setSuccessMessage(null);

    try {
      await api.post('/auth/register', {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        mobileNumber: mobileNumber.trim(),
        password
      });

      setSuccessMessage('Registration successful! Redirecting...');

      // Redirect after 1.5 seconds
      setTimeout(() => {
        window.location.href = '/login';
      }, 1500);

    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Registration failed. Please try again.';
      setErrors({ general: errorMessage });
      console.error('Registration Error:', error.response ? error.response.data : error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-layout">
      {/* 1. Header */}
      <div className="auth-header">
        <h1 className="auth-brand-title">MAB Jewels</h1>
        <p className="auth-brand-subtitle">Employee Registration</p>
      </div>

      {/* 2. Card */}
      <div className="auth-card" style={{ maxWidth: '500px' }}>
        <div className="auth-card-header">
          <h2 className="auth-card-title">Create Account</h2>
          <p className="auth-card-subtitle">Join the jewellery management system</p>
        </div>

        {errors.general && (
          <div className="auth-alert" style={{ background: '#ffebeb', color: '#d32f2f' }}>
            {errors.general}
          </div>
        )}

        {successMessage && (
          <div className="auth-alert" style={{ background: '#e8f5e9', color: '#2e7d32', borderColor: '#c8e6c9' }}>
            {successMessage}
          </div>
        )}

        <form onSubmit={onSubmit} noValidate>
          {/* Names Row */}
          <div style={{ display: 'flex', gap: '15px', marginBottom: '24px' }}>
            <div style={{ flex: 1 }}>
              <label htmlFor="firstName" className="auth-label">First Name</label>
              <div className="auth-input-wrapper">
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  className="auth-input"
                  style={errors.firstName ? { borderColor: '#d32f2f' } : {}}
                  value={firstName}
                  onChange={onChange}
                  placeholder="First name"
                  required
                  autoComplete="given-name"
                  disabled={loading}
                />
              </div>
              {errors.firstName && <span style={{ color: '#d32f2f', fontSize: '0.75rem', marginTop: '4px', display: 'block' }}>{errors.firstName}</span>}
            </div>

            <div style={{ flex: 1 }}>
              <label htmlFor="lastName" className="auth-label">Last Name</label>
              <div className="auth-input-wrapper">
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  className="auth-input"
                  style={errors.lastName ? { borderColor: '#d32f2f' } : {}}
                  value={lastName}
                  onChange={onChange}
                  placeholder="Last name"
                  required
                  autoComplete="family-name"
                  disabled={loading}
                />
              </div>
              {errors.lastName && <span style={{ color: '#d32f2f', fontSize: '0.75rem', marginTop: '4px', display: 'block' }}>{errors.lastName}</span>}
            </div>
          </div>

          {/* Email */}
          <div className="auth-form-group">
            <label htmlFor="email" className="auth-label">Email Address</label>
            <div className="auth-input-wrapper">
              <input
                type="email"
                id="email"
                name="email"
                className="auth-input"
                style={errors.email ? { borderColor: '#d32f2f' } : {}}
                value={email}
                onChange={onChange}
                placeholder="name@company.com"
                required
                autoComplete="email"
                disabled={loading}
              />
            </div>
            {errors.email && <span style={{ color: '#d32f2f', fontSize: '0.75rem', marginTop: '4px', display: 'block' }}>{errors.email}</span>}
          </div>

          {/* Mobile Number */}
          <div className="auth-form-group">
            <label htmlFor="mobileNumber" className="auth-label">Mobile Number</label>
            <div className="auth-input-wrapper">
              <input
                type="tel"
                id="mobileNumber"
                name="mobileNumber"
                className="auth-input"
                style={errors.mobileNumber ? { borderColor: '#d32f2f' } : {}}
                value={mobileNumber}
                onChange={onChange}
                placeholder="10-digit number"
                maxLength="10"
                required
                autoComplete="tel"
                disabled={loading}
              />
            </div>
            {errors.mobileNumber && <span style={{ color: '#d32f2f', fontSize: '0.75rem', marginTop: '4px', display: 'block' }}>{errors.mobileNumber}</span>}
          </div>

          {/* Passwords */}
          <div className="auth-form-group">
            <label htmlFor="password" className="auth-label">Password</label>
            <div className="auth-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                className="auth-input"
                style={errors.password ? { borderColor: '#d32f2f', paddingRight: '45px' } : { paddingRight: '45px' }}
                value={password}
                onChange={onChange}
                placeholder="Min 6 chars"
                required
                autoComplete="new-password"
                disabled={loading}
              />
              <button
                type="button"
                className="form-input-toggle-btn"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex="-1"
              >
                {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
              </button>
            </div>
            {errors.password && <span style={{ color: '#d32f2f', fontSize: '0.75rem', marginTop: '4px', display: 'block' }}>{errors.password}</span>}
          </div>

          <div className="auth-form-group">
            <label htmlFor="confirmPassword" className="auth-label">Confirm Password</label>
            <div className="auth-input-wrapper">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                className="auth-input"
                style={errors.confirmPassword ? { borderColor: '#d32f2f', paddingRight: '45px' } : { paddingRight: '45px' }}
                value={confirmPassword}
                onChange={onChange}
                placeholder="Confirm password"
                required
                autoComplete="new-password"
                disabled={loading}
              />
              <button
                type="button"
                className="form-input-toggle-btn"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                tabIndex="-1"
              >
                {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
              </button>
            </div>
            {errors.confirmPassword && <span style={{ color: '#d32f2f', fontSize: '0.75rem', marginTop: '4px', display: 'block' }}>{errors.confirmPassword}</span>}
          </div>

          <button
            type="submit"
            className="btn-vault"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Account'}
          </button>

          <div className="auth-footer">
            <span className="auth-link" style={{ color: '#888', cursor: 'default', textDecoration: 'none' }}>
              Already have an account?
            </span>{' '}
            <Link to="/login" className="auth-link" style={{ fontWeight: '700' }}>
              Sign In
            </Link>
          </div>
        </form>
      </div>
      {/* Footer Info */}
      <div style={{ textAlign: 'center', marginTop: '30px', color: '#888', fontSize: '0.8rem' }}>
        <p>&copy; {new Date().getFullYear()} MAB Jewels. Secure Admin Portal.</p>
      </div>
    </div>
  );
};

export default RegisterPage;

